// ============================================================
// Contrack AI — Step 5: building screen (the magic moment)
// Faithful port of the building half of the prototype's finale.jsx.
// NOTE: the prototype's step-6 dashboard is intentionally NOT ported —
// when the build completes we hand off (focus-pull) to the app's own
// existing dashboard. See OnboardingView's onComplete + the
// `.cob-dash-enter` animation in onboarding.css.
// ============================================================
import { useState, useEffect } from "react";
import { Logo, IconCheck, buildingLines, type OnboardingAnswers } from "./data";
import { useOnboardingLang } from "./i18n";

function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

export default function BuildingScreen({ answers, onDone }: { answers: OnboardingAnswers; onDone: () => void }) {
  const { copy } = useOnboardingLang();
  const lines = buildingLines(answers, copy);
  const [resolved, setResolved] = useState(0); // how many lines have resolved to check
  const [visible, setVisible] = useState(1);    // how many lines are shown

  useEffect(() => {
    const t: ReturnType<typeof setTimeout>[] = [];
    const STEP = 700;
    // reveal each line, then resolve spinner -> check, sequentially
    for (let i = 0; i < lines.length; i++) {
      t.push(setTimeout(() => setVisible(i + 1), i * STEP));
      t.push(setTimeout(() => setResolved(i + 1), i * STEP + 520));
    }
    // all checks done -> 500ms hold -> hand off (orchestrator runs the focus-pull)
    t.push(setTimeout(() => onDone(), (lines.length - 1) * STEP + 520 + 500));
    return () => t.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pct = Math.round((resolved / lines.length) * 100);

  return (
    <div className="building">
      <div className="building-inner">
        <div className="building-logo">
          <Logo scale={2.4} mode="pulse" />
        </div>
        <p className="building-heading">{copy.buildingHeading}</p>
        <div className="building-progress">
          <span className="building-progress-fill" style={{ width: pct + "%" }} />
        </div>
        <ul className="build-lines">
          {lines.slice(0, visible).map((ln, i) => {
            const done = i < resolved;
            return (
              <li key={i} className="build-line build-line-in">
                <span className="build-state">
                  {done ? (
                    <span className="build-check"><IconCheck size={14} style={{ color: "#fff" }} /></span>
                  ) : (
                    <Spinner />
                  )}
                </span>
                <span className={"build-text" + (done ? " build-text-done" : "")}>{ln}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
